import React, { useState, useMemo } from 'react';
import './PolicyManagement.css';

const MOCK_POLICIES = [
  { id: 1, policyNumber: 'CY-2026-001', insuredName: 'SecureNet Technologies Inc', product: 'Cyber E&O', premium: 48500, limit: 5000000, effectiveDate: '2026-01-15', status: 'active' },
  { id: 2, policyNumber: 'CY-2026-002', insuredName: 'DataGuard Solutions LLC', product: 'Cyber Liability', premium: 31200, limit: 2000000, effectiveDate: '2026-01-22', status: 'active' },
  { id: 3, policyNumber: 'CY-2025-089', insuredName: 'CloudSync Inc', product: 'Tech E&O', premium: 67500, limit: 10000000, effectiveDate: '2025-03-01', status: 'issued' },
  { id: 4, policyNumber: 'CY-2025-076', insuredName: 'MedTech Health Systems', product: 'Cyber & Privacy', premium: 128000, limit: 25000000, effectiveDate: '2025-02-15', status: 'expired' },
  { id: 5, policyNumber: 'CY-2026-003', insuredName: 'FinServe Payments Corp', product: 'Cyber Liability', premium: 89000, limit: 15000000, effectiveDate: '2026-02-01', status: 'active' },
  { id: 6, policyNumber: 'CY-2025-091', insuredName: 'RetailMax eCommerce', product: 'Cyber E&O', premium: 24500, limit: 1000000, effectiveDate: '2025-04-10', status: 'cancelled' },
  { id: 7, policyNumber: 'CY-2026-004', insuredName: 'LegalTech Partners LLP', product: 'Cyber & Privacy', premium: 52000, limit: 5000000, effectiveDate: '2026-02-10', status: 'active' },
  { id: 8, policyNumber: 'CY-2026-005', insuredName: 'EduLearn Platform Inc', product: 'Tech E&O', premium: 18900, limit: 2000000, effectiveDate: '2026-02-15', status: 'issued' },
  { id: 9, policyNumber: 'CY-2025-082', insuredName: 'Manufacturing IoT Corp', product: 'Cyber Liability', premium: 42000, limit: 5000000, effectiveDate: '2025-05-01', status: 'expired' },
  { id: 10, policyNumber: 'CY-2026-006', insuredName: 'Hospitality Cloud Inc', product: 'Cyber & Privacy', premium: 35600, limit: 3000000, effectiveDate: '2026-02-20', status: 'active' },
];

function formatCurrency(val) {
  if (val == null) return '—';
  return new Intl.NumberFormat('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(val);
}

function formatDate(dateStr) {
  if (!dateStr) return '—';
  return new Date(dateStr).toLocaleDateString('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
}

function getStatusBadgeClass(status) {
  switch (status?.toLowerCase()) {
    case 'active': return 'pom__badge pom__badge--active';
    case 'issued': return 'pom__badge pom__badge--issued';
    case 'expired': return 'pom__badge pom__badge--expired';
    case 'cancelled': return 'pom__badge pom__badge--cancelled';
    default: return 'pom__badge';
  }
}

export function PolicyManagement() {
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');

  const filteredPolicies = useMemo(() => {
    return MOCK_POLICIES.filter((p) => {
      const matchesSearch =
        !search.trim() ||
        (p.policyNumber || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.insuredName || '').toLowerCase().includes(search.toLowerCase()) ||
        (p.product || '').toLowerCase().includes(search.toLowerCase());
      const matchesStatus =
        statusFilter === 'all' || (p.status || '').toLowerCase() === statusFilter;
      return matchesSearch && matchesStatus;
    });
  }, [search, statusFilter]);

  const stats = useMemo(() => {
    const total = MOCK_POLICIES.length;
    const active = MOCK_POLICIES.filter((p) => p.status === 'active').length;
    const writtenPremium = MOCK_POLICIES.reduce((sum, p) => sum + (p.premium || 0), 0);
    const avgPremium = total > 0 ? Math.round(writtenPremium / total) : 0;
    return { total, active, writtenPremium, avgPremium };
  }, []);

  return (
    <div className="pom">
      <h1 className="pom__title">Policy Management</h1>

      {/* Stat cards */}
      <div className="pom__stats">
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Total Policies</span>
          <span className="pom__stat-value">{stats.total}</span>
        </div>
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Active</span>
          <span className="pom__stat-value pom__stat-value--green">{stats.active}</span>
        </div>
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Written Premium</span>
          <span className="pom__stat-value">{formatCurrency(stats.writtenPremium)}</span>
        </div>
        <div className="pom__stat-card card">
          <span className="pom__stat-label">Avg Premium</span>
          <span className="pom__stat-value">{formatCurrency(stats.avgPremium)}</span>
        </div>
      </div>

      {/* Search and filter */}
      <div className="pom__filters">
        <div className="pom__search-wrap">
          <input
            className="pom__search"
            type="text"
            placeholder="Search by policy #, insured, or product..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button className="pom__search-clear" onClick={() => setSearch('')}>
              &times;
            </button>
          )}
        </div>
        <select
          className="pom__filter-select"
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
        >
          <option value="all">All Statuses</option>
          <option value="active">Active</option>
          <option value="issued">Issued</option>
          <option value="expired">Expired</option>
          <option value="cancelled">Cancelled</option>
        </select>
      </div>

      {/* Table */}
      <div className="pom__section">
        <h2 className="pom__section-title">Policies</h2>
        <div className="pom__table-wrap card">
          <table className="pom__table">
            <thead>
              <tr>
                <th>Policy #</th>
                <th>Insured Name</th>
                <th>Product</th>
                <th>Premium</th>
                <th>Limit</th>
                <th>Effective Date</th>
                <th>Status</th>
              </tr>
            </thead>
            <tbody>
              {filteredPolicies.length > 0 ? (
                filteredPolicies.map((p) => (
                  <tr key={p.id} className="pom__table-row">
                    <td className="pom__table-policy">{p.policyNumber}</td>
                    <td className="pom__table-insured">{p.insuredName}</td>
                    <td>{p.product}</td>
                    <td className="pom__table-currency">{formatCurrency(p.premium)}</td>
                    <td className="pom__table-currency">{formatCurrency(p.limit)}</td>
                    <td className="pom__table-date">{formatDate(p.effectiveDate)}</td>
                    <td>
                      <span className={getStatusBadgeClass(p.status)}>
                        {p.status}
                      </span>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={7} className="pom__table-empty">
                    No policies match your filters.
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
